import React, { useState } from "react";
import { Minus, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/format";
import { toast } from "sonner";

const STATUS_BADGE = {
  low_stock: { label: "LOW STOCK", cls: "bg-amber-600/90 text-white" },
  sold_out: { label: "SOLD OUT", cls: "bg-destructive text-white" },
};

export function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const sizes = product.sizes || [];
  const [sizeIdx, setSizeIdx] = useState(sizes.length > 1 ? 1 : 0);
  const [qty, setQty] = useState(1);
  const soldOut = product.status === "sold_out";
  const size = sizes[sizeIdx] || sizes[0];
  const price = size ? size.price : 0;
  const badge = STATUS_BADGE[product.status];

  const add = () => {
    if (soldOut || !size) return;
    addItem({
      key: `${product.id}__${size.label}`,
      product_id: product.id,
      category: product.category,
      name: product.name,
      image: product.image,
      size_label: size.label,
      unit_price: price,
      quantity: qty,
      cookie_flavors: [],
    });
    toast.success(`${product.name} added to cart`);
    setQty(1);
  };

  return (
    <div
      className="gold-border-card rounded-xl overflow-hidden bg-card-surface flex flex-col animate-fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className={`h-full w-full object-cover transition-transform duration-500 hover:scale-105 ${soldOut ? "grayscale opacity-60" : ""}`}
        />
        {product.best_seller && !badge && (
          <span className="absolute top-3 left-3 btn-gold text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wide">
            BEST SELLER
          </span>
        )}
        {badge && (
          <span className={`absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wide ${badge.cls}`}>
            {badge.label}
          </span>
        )}
      </div>

      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <h3 className="font-serif text-cream text-lg sm:text-xl font-semibold leading-tight">{product.name}</h3>
        <p className="text-cream/60 text-sm mt-1.5 flex-1">{product.description}</p>

        {sizes.length > 0 && (
          <div className="mt-4">
            <div className="grid grid-cols-3 gap-1.5">
              {sizes.map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => setSizeIdx(i)}
                  data-testid={`size-${product.id}-${i}`}
                  className={`rounded-lg border px-1 py-2 text-center transition-all ${
                    sizeIdx === i
                      ? "border-gold bg-gold/15 text-gold"
                      : "border-gold/20 text-cream/60 hover:border-gold/50"
                  }`}
                >
                  <div className="text-[11px] font-medium leading-tight">{s.label.split(" ")[0]} {s.label.split(" ")[1]}</div>
                  <div className="font-mono text-xs mt-0.5">{money(s.price)}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-9 w-9 rounded-full border border-gold/40 flex items-center justify-center text-cream hover:bg-gold/10" data-testid={`qty-dec-${product.id}`}>
              <Minus size={15} />
            </button>
            <span className="font-mono w-6 text-center text-cream" data-testid={`qty-val-${product.id}`}>{qty}</span>
            <button onClick={() => setQty(qty + 1)} className="h-9 w-9 rounded-full border border-gold/40 flex items-center justify-center text-cream hover:bg-gold/10" data-testid={`qty-inc-${product.id}`}>
              <Plus size={15} />
            </button>
          </div>
          <span className="font-mono text-gold text-xl font-semibold" data-testid={`price-${product.id}`}>{money(price * qty)}</span>
        </div>

        <Button
          onClick={add}
          disabled={soldOut}
          data-testid={`add-to-cart-${product.id}`}
          className="btn-gold w-full mt-4 rounded-full py-5 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {soldOut ? "Sold Out" : (<span className="flex items-center gap-2"><Check size={16} /> Add to Cart</span>)}
        </Button>
      </div>
    </div>
  );
}
